export type RegionalDomain = "devo-FE" | "prod-EU" | "prod-US" | "prod-FE";

export type Domain = RegionalDomain | "Global";

/** Map keyed by regional domain with a "default" fallback. Regional keys are optional — unspecified domains fall back to "default". */
export type DomainMap<V> = Partial<Record<RegionalDomain, V>> & { default: V };

interface LambdaConfig {
  domainId: RegionalDomain;
  awsRegion: string;
  transactionSearchLambdaARN: string;
  ardServiceLambdaARN: string;
  billingGatewayServiceAPIInvokeLambdaARN: string;
  billingGatewayServiceBulkRedriveWorkflowLambdaARN: string;
  bartDLQMoverLambdaARN: string;
}

const BartLambdaConfig: LambdaConfig[] = [
  {
    domainId: "devo-FE",
    awsRegion: "us-west-2",
    transactionSearchLambdaARN:
      "arn:aws:lambda:us-west-2:444552806502:function:FuseBartTransactionSearchLambda-FE:live",
    ardServiceLambdaARN:
      "arn:aws:lambda:us-west-2:444552806502:function:FuseBartArdServiceLambda-FE",
    billingGatewayServiceAPIInvokeLambdaARN:
      "arn:aws:lambda:us-west-2:444552806502:function:FuseBartBillingGatewayServiceLambda-FE",
    billingGatewayServiceBulkRedriveWorkflowLambdaARN:
      "arn:aws:lambda:us-west-2:444552806502:function:FuseBartFBGSBulkRedriveLambda-FE",
    bartDLQMoverLambdaARN:
      "arn:aws:lambda:us-west-2:444552806502:function:FuseBartDLQMoverLambda-FE",
  },

  {
    domainId: "prod-FE",
    awsRegion: "us-west-2",
    transactionSearchLambdaARN:
      "arn:aws:lambda:us-west-2:517990620071:function:FuseBartTransactionSearchLambda-FE:live",
    ardServiceLambdaARN:
      "arn:aws:lambda:us-west-2:517990620071:function:FuseBartArdServiceLambda-FE",
    billingGatewayServiceAPIInvokeLambdaARN:
      "arn:aws:lambda:us-west-2:517990620071:function:FuseBartBillingGatewayServiceLambda-FE",
    billingGatewayServiceBulkRedriveWorkflowLambdaARN:
      "arn:aws:lambda:us-west-2:517990620071:function:FuseBartFBGSBulkRedriveLambda-FE",
    bartDLQMoverLambdaARN:
      "arn:aws:lambda:us-west-2:517990620071:function:FuseBartDLQMoverLambda-FE",
  },

  {
    domainId: "prod-US",
    awsRegion: "us-east-1",
    transactionSearchLambdaARN:
      "arn:aws:lambda:us-east-1:517990620071:function:FuseBartTransactionSearchLambda-NA:live",
    ardServiceLambdaARN:
      "arn:aws:lambda:us-east-1:517990620071:function:FuseBartArdServiceLambda-NA",
    billingGatewayServiceAPIInvokeLambdaARN:
      "arn:aws:lambda:us-east-1:517990620071:function:FuseBartBillingGatewayServiceLambda-NA",
    billingGatewayServiceBulkRedriveWorkflowLambdaARN:
      "arn:aws:lambda:us-east-1:517990620071:function:FuseBartFBGSBulkRedriveLambda-NA",
    bartDLQMoverLambdaARN:
      "arn:aws:lambda:us-east-1:517990620071:function:FuseBartDLQMoverLambda-NA",
  },

  {
    domainId: "prod-EU",
    awsRegion: "eu-west-1",
    transactionSearchLambdaARN:
      "arn:aws:lambda:eu-west-1:517990620071:function:FuseBartTransactionSearchLambda-EU:live",
    ardServiceLambdaARN:
      "arn:aws:lambda:eu-west-1:517990620071:function:FuseBartArdServiceLambda-EU",
    billingGatewayServiceAPIInvokeLambdaARN:
      "arn:aws:lambda:eu-west-1:517990620071:function:FuseBartBillingGatewayServiceLambda-EU",
    billingGatewayServiceBulkRedriveWorkflowLambdaARN:
      "arn:aws:lambda:eu-west-1:517990620071:function:FuseBartFBGSBulkRedriveLambda-EU",
    bartDLQMoverLambdaARN:
      "arn:aws:lambda:eu-west-1:517990620071:function:FuseBartDLQMoverLambda-EU",
  },
];

const BART_PORTAL_ORIGIN: DomainMap<string> = {
  "devo-FE": "https://beta.bart-portal.fuse.amazon.dev",
  "prod-FE": "https://prod-jp.bart-portal.fuse.amazon.dev",
  "prod-US": "https://prod-us.bart-portal.fuse.amazon.dev",
  "prod-EU": "https://prod-eu.bart-portal.fuse.amazon.dev",
  default: "https://beta.bart-portal.fuse.amazon.dev",
};

/**
 * Returns the Bart portal base URL (origin) for the given domain.
 * Append a path to build any page link, e.g. `${getBartPortalOrigin(domain)}/getfixedfeeconfig/${id}`
 */
export const getBartPortalOrigin = (domain: RegionalDomain): string =>
  BART_PORTAL_ORIGIN[domain] ?? BART_PORTAL_ORIGIN.default;

/**
 * Builds a Bart portal link for the given target domain and path.
 * Returns a relative path when the target matches the current portal origin
 * or when running on localhost (local dev), otherwise returns an absolute
 * cross-region URL.
 */
export const getBartPortalUrl = (
  targetDomain: RegionalDomain,
  path: string,
): string => {
  if (typeof window === "undefined")
    return `${getBartPortalOrigin(targetDomain)}${path}`;
  const currentOrigin = window.location.origin;
  const targetOrigin = getBartPortalOrigin(targetDomain);
  if (
    currentOrigin === targetOrigin ||
    currentOrigin.startsWith("http://localhost")
  ) {
    return path;
  }
  return `${targetOrigin}${path}`;
};

export const getBartLambdaConfig = (domainId: RegionalDomain): LambdaConfig => {
  return (
    BartLambdaConfig.find((element) => element.domainId === domainId) || {
      domainId: "devo-FE",
      awsRegion: "us-west-2",
      transactionSearchLambdaARN:
        "arn:aws:lambda:us-west-2:444552806502:function:FuseBartTransactionSearchLambda-FE:live",
      ardServiceLambdaARN:
        "arn:aws:lambda:us-west-2:444552806502:function:FuseBartArdServiceLambda-FE",
      billingGatewayServiceAPIInvokeLambdaARN:
        "arn:aws:lambda:us-west-2:444552806502:function:FuseBartBillingGatewayServiceLambda-FE",
      billingGatewayServiceBulkRedriveWorkflowLambdaARN:
        "arn:aws:lambda:us-west-2:444552806502:function:FuseBartFBGSBulkRedriveLambda-FE",
      bartDLQMoverLambdaARN:
        "arn:aws:lambda:us-west-2:444552806502:function:FuseBartDLQMoverLambda-FE",
    }
  );
};
